import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  CarouselProvider, Slider, Slide, DotGroup, ButtonBack, ButtonNext,
} from 'pure-react-carousel';
import {
  Button,
  Icon,
  TranslationProvider,
  utils,
  Divider,
} from '@resy/ui-kitchen';
import { connect } from 'react-redux';
import Chevron from '../../../../../ui-kitchen/src/svg/icons/ic_chevron-left_20x20.svg';
import {
  getAnnouncements,
  getAnnouncementParams,
} from '../../containers/AnnouncementContainer/AnnouncementAPI';
import MegaphoneSVG from '../../../images/icons/megaphone.svg';
import 'pure-react-carousel/dist/react-carousel.es.css';
import './ResySpotlight.scss';

import announcementEvents from '../../../../../ui-kitchen/src/components/Announcement/announcementEvents';
import { getUuid } from '../../../../../ui-kitchen/src/utils';
import DepthTracker from '../DepthTracker/DepthTracker';
import { EVENT_TYPE } from '../../../../../ui-kitchen/src/components/Announcement/announcementTypes';

const { DISPLAYED } = announcementEvents;

const ResySpotlight = ({ Analytics, locationCode, deviceId }) => {
  const [announcements, setAnnouncements] = useState([]);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);

  const fetchAnnouncements = async (params) => {
    const response = await getAnnouncements(params);
    const announcementsPayload = response?.filter((item) => item?.display?.web?.show_ad);
    setAnnouncements(announcementsPayload);
  };

  useEffect(() => {
    const [uuid, removeCookieListener] = getUuid(deviceId);
    const params = {
      ...getAnnouncementParams(),
      deviceId: uuid,
      locationCode,
    };
    fetchAnnouncements(params);

    return () => {
      removeCookieListener();
    };
  }, [locationCode, deviceId]);

  const setSlideIndex = (index) => {
    setCurrentSlideIndex(index);
  };

  const handleCTAClick = (id, url, title, cta) => {
    Analytics.event(EVENT_TYPE.CLICK_ANNOUNCEMENT, {
      context: 'homepage',
      type: 'ad',
      title,
      id,
      cta,
    });

    window.open(url, '_blank');
  };

  const renderBackButton = () => {
    const prevSlideIndex = currentSlideIndex - 1;
    let newSlideIndex = prevSlideIndex;
    if (!announcements[prevSlideIndex]) {
      newSlideIndex = currentSlideIndex;
    }

    return (
      <ButtonBack onClick={() => {
        utils.createChainedFunction(
          setSlideIndex(newSlideIndex)
        );
      }}
      >
        <div className="ResySpotlight__carousel-button-container">
          <Icon
            name="Chevron"
            component={Chevron}
          />
        </div>
      </ButtonBack>
    );
  };

  const renderNextButton = () => {
    const nextSlideIndex = currentSlideIndex + 1;
    let newSlideIndex = nextSlideIndex;
    if (!announcements[nextSlideIndex]) {
      newSlideIndex = currentSlideIndex;
    }

    return (
      <ButtonNext onClick={() => {
        utils.createChainedFunction(
          setSlideIndex(newSlideIndex)
        );
      }}
      >
        <div className="ResySpotlight__carousel-button-container">
          <Icon
            name="Chevron"
            component={Chevron}
          />
        </div>
      </ButtonNext>
    );
  };

  const renderDots = () => (
    <DotGroup />
  );

  if (!announcements || announcements.length === 0) {
    return null;
  }

  return (
    <div className="ResySpotlight">
      <Divider />
      <div className="ResySpotlight__header--content">
        <Icon component={MegaphoneSVG} name="megaphone" />
        <TranslationProvider translationKey="resy_spotlight">
          <span className="ResySpotlight__header__title">
            Resy Spotlight
          </span>
        </TranslationProvider>
      </div>

      <div className="ResySpotlight__carousel">
        <CarouselProvider
          isIntrinsicHeight
          infinite
          naturalSlideHeight={250}
          naturalSlideWidth={1075}
          totalSlides={announcements.length}
          touchEnabled
        >
          <div className="ResySpotlight__carousel__container">
            {announcements.length > 1 && renderBackButton()}

            <Slider tabIndex={-1}>

              {announcements.map((slide, index) => (
                <Slide index={index} tabIndex={-1} key={slide.id}>
                  <DepthTracker
                    Analytics={Analytics}
                    eventName={DISPLAYED}
                    context="homepage"
                    eventAsset={slide.display.title}
                    title={slide.display.title}
                    id={slide.id}
                  >
                    <img
                      alt={slide.display.title}
                      className="ResySpotlight__carousel__slide-image"
                      key={slide.id}
                      src={slide.display.image}
                      tabIndex={-1}
                    />
                  </DepthTracker>
                  <div className="ResySpotlight__carousel__slide-text">
                    <div className="ResySpotlight__carousel__slide-text--heading">
                      {slide.display.heading}
                    </div>
                    <div className="ResySpotlight__carousel__slide-text--title">
                      {slide.display.title}
                    </div>
                    <div className="ResySpotlight__carousel__slide-text--content">
                      {slide.display.content}
                    </div>

                    <div className="ResySpotlight__carousel__slide-text__button-group">
                      <Button
                        className="ResySpotlight__carousel__slide-text--cta-primary"
                        variant="primary"
                        sm
                        onClick={() => handleCTAClick(
                          slide.id,
                          slide.action?.data?.url,
                          slide.display.title,
                          'primary'
                        )}
                      >
                        {slide.display.action_text}
                      </Button>
                      {slide.display.secondary_action_text
                        && (
                        <Button
                          className="ResySpotlight__carousel__slide-text--cta-secondary"
                          variant="secondary"
                          sm
                          onClick={() => handleCTAClick(
                            slide.id,
                            slide.secondary_action?.data?.url,
                            slide.display.title,
                            'secondary'
                          )}
                        >
                          {slide.display.secondary_action_text}
                        </Button>
                        )}
                    </div>

                  </div>
                </Slide>
              ))}
            </Slider>

            {announcements.length > 1 && renderNextButton()}
          </div>
          {announcements.length > 1 && renderDots()}
        </CarouselProvider>
      </div>
      <Divider />
    </div>
  );
};

const resySpotlightPropTypes = {
  $rootScope: PropTypes.shape({
    $on: PropTypes.func.isRequired,
  }).isRequired,
  Analytics: PropTypes.shape({
    event: PropTypes.func.isRequired,
  }).isRequired,
  locationCode: PropTypes.string,
  deviceId: PropTypes.string.isRequired,
};

ResySpotlight.propTypes = resySpotlightPropTypes;

ResySpotlight.defaultProps = {
  locationCode: 'ny',
};

export const mapStateToProps = ({ resyConfig: { deviceId } }) => ({ deviceId });

export { resySpotlightPropTypes };
export { ResySpotlight as ComponentUnderTest };
export default connect(mapStateToProps)(ResySpotlight);
